import { afterRenderEffect, booleanAttribute, computed, Directive, ElementRef, inject, input } from '@angular/core'
import { DomSanitizer } from '@angular/platform-browser'
import { Router } from '@angular/router'

import { LINKS } from 'app/core/cms/links.token'
import { CURRENT_LANG } from 'app/core/i18n/language.token'

import { LexicalRichText } from './lexical.type'
import { lexicalToHtml, RT_LINK_TYPE_ATTR } from './lexical-to-html.function'

function transformRichText(richtext: object | null): LexicalRichText {
  if (!richtext) {
    return {
      root: {
        children: [],
        direction: 'ltr',
        format: '',
        indent: 0,
        type: 'root',
        version: 1,
      },
    }
  }
  return richtext as LexicalRichText
}

@Directive({
  selector: '[paiLexicalRichText]',
  host: {
    '[innerHTML]': 'html()',
    class: 'pai-richtext',
    '[class.pai-richtext--centered]': 'this.centered()',
  },
})
export class LexicalRichTextDirective {
  readonly data = input.required({ alias: 'paiLexicalRichText', transform: transformRichText })
  readonly centered = input(false, { transform: booleanAttribute })

  private readonly router = inject(Router)
  private readonly san = inject(DomSanitizer)
  private readonly lang = inject(CURRENT_LANG)
  private readonly links = inject(LINKS)

  // when not bypassing security, angular removes the 'data' attributes we use for link handling
  protected readonly html = computed(() =>
    this.san.bypassSecurityTrustHtml(lexicalToHtml(this.data(), this.lang, this.links)),
  )
  private readonly elRef = inject<ElementRef<HTMLElement>>(ElementRef)

  constructor() {
    // afterRender since we need the rendered dom.
    //   alternatively, we could use a DomObserver
    afterRenderEffect((onCleanup) => {
      void this.html()

      // since the richtext might contain internal links as well but is not  within the angular context,
      // we need imitate the functionality of the [routerLink] directive
      const internalLinks: HTMLLinkElement[] = Array.from(
        this.elRef.nativeElement.querySelectorAll(`a[${RT_LINK_TYPE_ATTR}='internal']:not([target='_blank'])`),
      )

      for (const link of internalLinks) {
        link.addEventListener('click', this.onLinkClick)
      }

      onCleanup(() => {
        for (const link of internalLinks) {
          link.removeEventListener('click', this.onLinkClick)
        }
      })
    })
  }

  private readonly onLinkClick = (ev: MouseEvent) => {
    if (ev.button !== 0 || ev.ctrlKey || ev.metaKey || ev.shiftKey) {
      return true
    }
    // prevent hard reload
    ev.preventDefault()

    // there will always be an anchor since we attached the listener to it
    const anchor = (ev.target as HTMLElement).closest('a') as HTMLAnchorElement

    // hint: do not use anchor.href directly - instead read with `getAttribute`; otherwise the domain is prepended
    const href = anchor.getAttribute('href') as string
    void this.router.navigateByUrl(href)
    return false
  }
}
